import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useCart } from '../../contexts/CartContext';
import { checkoutAPI, type CheckoutData } from '../../api/checkout';
import AddressForm from '../../components/address/AddressForm';
import CartSummary from '../../components/customer/CartSummary';
import {
  ArrowLeft,
  Shield,
  Truck,
  CreditCard,
  Loader2,
  AlertCircle,
  CheckCircle,
  MapPin,
  UserCheck,
  Home,
  Plus,
  ChevronRight,
  Globe,
  Lock,
  Package,
  Navigation,
  Building,
  Star,
  Edit2,
  X,
  Zap,
  Award,
  Clock,
  Phone,
  User
} from 'lucide-react';
import { useAddress } from '../../contexts/AddressContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrders } from '../../contexts/OrdersContext';
import { userAPI } from '../../api/user';
import toast, { showToast } from '../../utils/toast';
import addressAPI from '../../api/addresses';
import DeliveryOptions from '../../components/checkout/DeliveryOptions';
import { ordersAPI } from '../../api/orders';
import type { DeliveryOption } from '../../types';
import type { CartItem } from '../../types/cart';
import ReviewStep from '../../components/checkout/ReviewStep';
import { formatPhoneNumber } from '../../utils/PhoneFormatter';

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { addOrder } = useOrders();
  const { cart, clearCart, validateCart } = useCart();
  const { addresses, defaultAddress, isLoading: addressLoading, addAddress, fetchAddresses, fetchAddressesIfNeeded } = useAddress();
  const { user, isLoading: userLoading } = useAuth();

  const [step, setStep] = useState<'address' | 'review' | 'processing'>('address');

  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<DeliveryOption | null>(null);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitializingAddresses, setIsInitializingAddresses] = useState(true);
  const [error, setError] = useState('');
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [showAllAddresses, setShowAllAddresses] = useState(false);
  const [verificationModal, setVerificationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    buttonText: '',
    buttonAction: () => { },
    canProceed: false
  });

  // Form states
  const [customerNotes, setCustomerNotes] = useState('');

  // Address form
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddressData, setNewAddressData] = useState<any>(null);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [cities, setCities] = useState<Array<{ id: number; name: string; delivery_fee: number }>>([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // Verification states
  const [isFullyVerified, setIsFullyVerified] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(true);
  const errorRef = useRef<HTMLDivElement>(null);

  // Progress steps configuration
  const steps = [
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'review', label: 'Review', icon: Package },
    { id: 'processing', label: 'Complete', icon: CheckCircle }
  ];

  // Check verification status and initialize
  // Update the verification check
  useEffect(() => {
    const checkVerificationAndInitialize = async () => {
      setIsCheckingVerification(true);

      if (cart.items.length === 0) {
        navigate('/cart');
        return;
      }

      if (!user) {
        setIsCheckingVerification(false);
        return;
      }

      if (isInitializingAddresses) {
        return;
      }

      // Check verification statuses
      const isIdApproved = user.id_verification_status === 'approved';
      const homeAddresses = addresses.filter(addr => addr.label?.toLowerCase() === 'home');
      const hasVerifiedHomeAddress = homeAddresses.some(addr => addr.address_verification_status === 'approved');

      // For submission checks, we need at least one home address with location
      const hasAnyHomeAddress = homeAddresses.length > 0;
      const hasAnyHomeWithLocation = homeAddresses.some(addr =>
        addr.latitude != null && addr.longitude != null
      );

      // Track rejected home addresses
      const hasRejectedHomeAddress = homeAddresses.some(addr =>
        addr.address_verification_status === 'rejected'
      );

      // Track pending home addresses
      const hasPendingHomeAddress = homeAddresses.some(addr =>
        addr.address_verification_status === 'pending'
      );

      // Check submission status
      const hasSubmittedId = user.id_verification_status !== 'not_uploaded';

      const isFullyVerified = isIdApproved && hasVerifiedHomeAddress;
      setIsFullyVerified(isFullyVerified);

      if (isFullyVerified) {
        // Fully verified - proceed normally
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
        } else if (addresses.length > 0) {
          const firstVerified = addresses.find(addr => addr.is_verified);
          if (firstVerified) {
            setSelectedAddressId(firstVerified.id);
          }
        }
        setIsCheckingVerification(false);
        return;
      }

      // Determine what needs to be done
      let redirectTab = '';
      let actionRequired = [];
      let canProceed = true;

      // ID verification check
      if (!isIdApproved) {
        if (!hasSubmittedId) {
          actionRequired.push('Upload your ID document');
          redirectTab = 'verification';
          canProceed = false;
        } else if (user.id_verification_status === 'pending') {
          actionRequired.push('ID verification pending');
          // Don't redirect, just inform
        } else if (user.id_verification_status === 'rejected') {
          actionRequired.push('Your ID was rejected - please re-upload');
          redirectTab = 'verification';
          canProceed = false;
        }
      }

      // Address verification check
      if (!hasVerifiedHomeAddress) {
        if (!hasAnyHomeAddress) {
          actionRequired.push('Add a HOME address');
          redirectTab = 'addresses';
          canProceed = false;
        } else if (hasPendingHomeAddress) {
          actionRequired.push('HOME address verification pending');
          // Don't redirect, just inform
        } else if (!hasAnyHomeWithLocation) {
          actionRequired.push('Capture location for your HOME address');
          redirectTab = 'addresses';
          canProceed = false;
        } else if (hasRejectedHomeAddress) {
          actionRequired.push('Your HOME address was rejected - please update it');
          redirectTab = 'addresses';
          canProceed = false;
        }
      }

      // Build dynamic message
      let title, message, buttonText, buttonAction;

      if (!canProceed) {
        // Action required - redirect

        title = "⚠️ Verification Required";
        message = `To complete your purchase, please:\n${actionRequired.map(a => `• ${a}`).join('\n')}`;
        buttonText = "Go to Profile";
        buttonAction = () => navigate(`/profile?tab=${redirectTab}`);
      } else {
        // All submitted, waiting for approval
        title = "Verification In Progress";
        message = `Your information has been submitted and is being reviewed:\n${actionRequired.map(a => `• ${a}`).join('\n')
          }\n\nYou will receive a notification once verified.`;
        buttonText = "Continue Shopping";
        buttonAction = () => navigate('/products');
      }

      setIsCheckingVerification(false);
      setVerificationModal({
        isOpen: true,
        title,
        message,
        buttonText,
        buttonAction,
        canProceed
      });
    };

    checkVerificationAndInitialize();
  }, [cart.items.length, navigate, user, defaultAddress, isInitializingAddresses, addresses]);

  // Fetch cities for address form
  useEffect(() => {
    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const response = await addressAPI.getCities();
        if (response.data.results) {
          setCities(response.data.results);
        }
        console.log(response)
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      } finally {
        setLoadingCities(false);
      }
    };
    loadCities();
  }, []);

  useEffect(() => {
    const initializeAddresses = async () => {
      try {
        await fetchAddressesIfNeeded();
      } catch (error) {
        console.error('Failed to fetch addresses:', error);
      } finally {
        setIsInitializingAddresses(false);
      }
    };
    initializeAddresses();
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      const response = await ordersAPI.getDeliveryOptions({
        items: cart.items,
        address_id: selectedAddressId
      });
      setDeliveryOptions(response.data.options);
    };

    if (selectedAddressId) {
      fetchOptions();
    }
  }, [selectedAddressId]);

  // Scroll to error when it changes
  useEffect(() => {
    if (error && errorRef.current) {
      setTimeout(() => {
        errorRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [error]);

  const handleAddressFormChange = (data: any, isValid: boolean) => {
    setNewAddressData(data);
    setIsAddressValid(isValid);
  };

  const handleAddressFormSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      const response = await addAddress(data);
      if (response) {
        await fetchAddresses();
        setSelectedAddressId(response.id);
        setShowAddressForm(false);
        setNewAddressData(null);
        toast.success('Address added successfully');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to add address');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToReview = () => {
    if (!selectedAddressId && !newAddressData) {
      setError('Please select or add a delivery address');
      return;
    }

    setStep('review');
    setError('');
  };

  const handlePlaceOrder = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      // Validate cart
      const { valid, errors } = await validateCart();
      if (!valid) {
        setError(`Cart validation failed: ${errors.join(', ')}`);
        setIsSubmitting(false);
        return;
      }

      // Final verification check
      if (!isFullyVerified) {
        setError('Please complete verification before placing an order.');
        setIsSubmitting(false);
        return;
      }

      const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
      const selectedCity = cities.find(c => c.id === (selectedAddress?.city || newAddressData?.city_id));

      // Prepare order data matching your new interface
      const orderData = {
        // Customer Information
        customer_name: selectedAddress?.recipient_name || user?.full_name || '',
        customer_phone: user?.phone || '',
        customer_notes: customerNotes || undefined,

        // Shipping Information (Denormalized)
        shipping_address_id: selectedAddress?.id || null,
        shipping_address_location_name: selectedAddress?.location_name || null,
        shipping_house_number: selectedAddress?.house_number || newAddressData?.house_number || '',
        shipping_address_line_1: selectedAddress?.address_line_1 || newAddressData?.address_line_1 || '',
        shipping_address_line_2: selectedAddress?.address_line_2 || newAddressData?.address_line_2,
        shipping_city_id: selectedAddress?.city || parseInt(newAddressData?.city) || 0,
        shipping_city_name: selectedCity?.name || '',
        shipping_address_label: selectedAddress?.label || newAddressData?.label || '',
        shipping_recipient_name: selectedAddress?.recipient_name || user?.full_name || '',
        shipping_contact_number: selectedAddress?.contact_number_1 || user?.phone || '',
        shipping_contact_number_2: selectedAddress?.contact_number_2 || user?.phone || '',
        shipping_delivery_note: selectedAddress?.delivery_note || newAddressData?.delivery_note,
        delivery_option: selectedDeliveryOption?.type || 'economic',
        delivery_fee: selectedDeliveryOption?.fee || 0,

        // Payment Info
        payment_method: 'cash_on_delivery',
        payment_status: 'pending',

        // Status
        status: 'pending',

        // Generate tracking number
        tracking_number: `TRK${Date.now()}${Math.floor(Math.random() * 1000)}`,

        // Cart items - optimized for your structure
        items: cart.items.map(item => ({
          product_id: item.product_id,
          product_variant_id: item.product_variant_id,
          product_name: item.product_title,
          product_title: item.product_title,
          product_sku: item.sku,
          product_image: item.image_url,
          product_variant_name: item.variant_name,
          price_at_sale: item.price,
          quantity: item.quantity,
          vendor_id: item.vendor_id || null,
          vendor_name: item.vendor_name || '',
          additional_images: item.additional_images || [],

          // Optional: Include warehouse info if your system needs it
          metadata: {
            warehouse_id: item.warehouse_id,
            // max_quantity: item.max_quantity,
            available_quantity: item.available_quantity
          }
        }))
      };


      // Create order via API
      const response = await checkoutAPI.createOrder(orderData);

      if (response.data) {
        // Clear cart on success
        const createdOrder = response.data;
        addOrder(createdOrder);

        clearCart();

        // Navigate to order confirmation
        navigate(`/order-confirmation/${response.data.order_number}`);
      } else {
        setError(response.error || 'Failed to create order. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      console.error('Checkout error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleOptionsLoaded = (options: DeliveryOption[]) => {
    if (!options || options.length === 0) return;

    // If there's only one option, select it regardless of type
    if (options.length === 1) {
      setSelectedDeliveryOption(options[0]);
      return;
    }

    // Otherwise, prefer economic if available
    const economic = options.find(opt => opt.type === 'economic');
    if (economic) {
      setSelectedDeliveryOption(economic);
    } else {
      setSelectedDeliveryOption(options[0]);
    }
  };

  const handleAddressSelect = (addressId: number) => {
    setSelectedAddressId(addressId);
    setError('');
  };

  // Loading state
  // if (cart.items.length === 0 || userLoading || addressLoading || isCheckingVerification) {
  //   return (
  //       <div className="min-h-[60vh] flex items-center justify-center">
  //         <div className="text-center">
  //           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
  //           <p className="mt-4 text-gray-600">Loading checkout...</p>
  //         </div>
  //       </div>
  //   );
  // } //causes a flickering effect when the verification modal is triggered, so we may have to move the loading state inside the modal condition

  // Calculate current step index
  const currentStepIndex = steps.findIndex(s => s.id === step);

  // Address card component
  const AddressCard = ({
    address,
    isSelected = false,
    onClick,
  }: {
    address: any;
    isSelected?: Boolean;
    onClick?: () => void;
  }) => {
    const isHome = address.label?.toLowerCase() === 'home';
    const isWork = address.label?.toLowerCase() === 'work';
    const isDefault = address.is_default;

    return (
      <div
        onClick={onClick}
        className={`relative transition-all duration-300 transform hover:scale-[1.02] cursor-pointer
          ${isSelected
            ? 'border-2 border-blue-500 shadow-lg shadow-blue-100'
            : 'border border-gray-200 hover:border-blue-300 hover:shadow-md'
          }`}
      >
        {/* Default badge - top left */}
        {isDefault && (
          <div className="absolute -top-2 -left-2 z-10">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
              Default
            </div>
          </div>
        )}

        {/* Selected indicator - top right */}
        {isSelected && (
          <div className="absolute -top-3 -right-3 z-10">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-2 rounded-full shadow-lg">
              <CheckCircle className="w-5 h-5" />
            </div>
          </div>
        )}

        {/* Card content */}
        <div className={`p-5 rounded-xl ${isSelected && 'bg-gradient-to-br from-blue-50 to-white'
          }`}>
          {/* Header with icon and label */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${isHome
                ? 'bg-gradient-to-r from-green-100 to-green-50 text-green-600'
                : isWork
                  ? 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600'
                  : 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-600'
                }`}>
                {isHome ? <Home className="w-5 h-5" /> :
                  isWork ? <Building className="w-5 h-5" /> :
                    <MapPin className="w-5 h-5" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`font-bold text-gray-900`}>
                    {address.label}
                  </span>
                  {isDefault && (
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-full">
                      ★
                    </span>
                  )}
                </div>
                <p className={`text-sm mt-1 text-gray-500`}>
                  {address.recipient_name || user?.full_name}
                </p>
              </div>
            </div>
          </div>

          {/* Address details */}
          <div className="mb-4">
            <p className={`font-medium mb-1 text-gray-800`}>
              {address.house_number} {address.address_line_1}
            </p>
            {address.address_line_2 && (
              <p className={`text-sm mb-1 text-gray-600`}>
                {address.address_line_2}
              </p>
            )}
            <div className={`flex items-center gap-2 text-sm text-gray-500`}>
              <MapPin className="w-4 h-4" />
              <span>{address.city}</span>
            </div>
          </div>

          {/* Delivery note */}
          {address.delivery_note && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <p className={`text-sm text-gray-600`}>
                <span className={`font-medium`}>📝 Note:</span> {address.delivery_note}
              </p>
            </div>
          )}

          {/* Footer with verification and contact */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              {/* Verification badge */}
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${address.is_verified
                ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800'
                : 'bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800'
                }`}>
                {address.is_verified ? (
                  <>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </>
                ) : (
                  <>
                    <Clock className="w-3 h-3 mr-1" />
                    Pending
                  </>
                )}
              </span>

              {/* Contact info */}
              {address.contact_number_1 && (
                <div className={`flex items-center gap-1 text-xs text-gray-500`}>
                  <Phone className="w-3 h-3" />
                  <span>{address.contact_number_1}</span>
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div className="flex items-center gap-2">
              {isSelected && (
                <div className="text-xs font-bold text-blue-600 animate-pulse">
                  ✓ Selected
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Glow effect for selected card */}
        {isSelected && (
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 to-transparent pointer-events-none"></div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Progress Steps */}
        <div className="mb-8 lg:mb-12">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Checkout</h1>
            <div className="text-sm text-gray-500 hidden sm:block">
              Step {currentStepIndex + 1} of {steps.length}
            </div>
          </div>

          {/* Progress bar */}
          <div className="relative">
            <div className="h-2 bg-gradient-to-r from-gray-200 to-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
              </div>
            </div>

            {/* Step indicators */}
            <div className="flex justify-between mt-3">
              {steps.map((stepItem, index) => {
                const isCompleted = index <= currentStepIndex;
                const StepIcon = stepItem.icon;

                return (
                  <div key={stepItem.id} className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200'
                      : 'bg-gray-100 text-gray-400'
                      }`}>
                      {isCompleted && index < currentStepIndex ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`mt-2 text-xs font-medium transition-colors ${isCompleted ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                      {stepItem.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div ref={errorRef} className="mb-6 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-2xl p-4 animate-fadeIn">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Address Selection */}
            {step === 'address' && (
              <div className="space-y-6">
                {/* Delivery Address Section */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
                  <div className="p-6 sm:p-8">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
                          <MapPin className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900">Delivery Address</h2>
                          <p className="text-gray-600 mt-2">
                            {selectedAddressId ? 'Your default address is selected' : 'Select where to deliver your order'}
                          </p>
                        </div>
                      </div>

                      {/* Verified Badge */}
                      <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-100 border border-green-200 rounded-full">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">Verified Account</span>
                      </div>
                    </div>

                    {/* Default Address Preview */}
                    {defaultAddress && !showAllAddresses && (
                      <div className="mb-6">
                        <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl p-4 sm:p-6">
                          {/* Header - Mobile optimized */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-start gap-3">
                              <div className="p-2 sm:p-3 bg-gradient-to-r from-blue-100 to-blue-50 rounded-lg mt-1">
                                <Home className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                              </div>
                              <div className="flex-1">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                                  <span className="text-lg sm:text-xl font-bold text-gray-900">{defaultAddress.label}</span>
                                  <span className="self-start sm:self-auto px-2 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold rounded-full">
                                    Default
                                  </span>
                                </div>
                                <p className="text-sm sm:text-base text-gray-600">This is your preferred delivery address</p>
                              </div>
                            </div>
                          </div>

                          {/* Address details - Mobile optimized */}
                          <div className="mb-4">
                            <p className="text-base sm:text-lg font-medium text-gray-800 leading-tight">
                              {defaultAddress.house_number} {defaultAddress.address_line_1}
                            </p>
                            {defaultAddress.address_line_2 && (
                              <p className="text-sm text-gray-600 mt-1">{defaultAddress.address_line_2}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2 text-gray-500">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="text-sm">{defaultAddress.city_details.name}</span>
                            </div>
                          </div>

                          {/* Contact info - Responsive grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-blue-100">
                            <div className="flex items-center gap-3">
                              <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs sm:text-sm text-gray-500">Recipient</p>
                                <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                                  {defaultAddress.recipient_name || user?.full_name}
                                </p>
                              </div>
                            </div>
                            {/* Primary Contact */}
                            {defaultAddress.contact_number_1 && (
                              <div className="flex items-center gap-3">
                                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs sm:text-sm text-gray-500">Primary Contact</p>
                                  <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                                    {formatPhoneNumber(defaultAddress.contact_number_1)}
                                  </p>
                                </div>
                              </div>
                            )}
                            {/* Secondary Contact - separate row if exists */}
                            {defaultAddress.contact_number_2 && (
                              <div className="flex items-center gap-3 pt-2">
                                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs sm:text-sm text-gray-500">Secondary Contact</p>
                                  <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                                    {formatPhoneNumber(defaultAddress.contact_number_2)}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Action buttons - Mobile optimized */}
                          <div className="flex flex-col gap-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-blue-100">
                            <button
                              onClick={handleProceedToReview}
                              className="px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-sm transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
                            >
                              Deliver to This Address
                              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 ml-2 rotate-180 flex-shrink-0" />
                            </button>
                            <button
                              onClick={() => setShowAllAddresses(true)}
                              className="px-4 py-2.5 sm:px-6 sm:py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
                            >
                              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                              Select Different Address
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* All Addresses View (shown when user selects "Select a Different Address") */}
                    {showAllAddresses && (
                      <div className="space-y-6">

                        {/* Saved Addresses Grid */}
                        {addresses.length > 0 && (
                          <div>
                            <div className="mb-6">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                                  Your Saved Addresses
                                </h3>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm text-gray-500">
                                    {addresses.length} total addresses
                                  </span>
                                </div>
                              </div>
                              <p className="text-gray-600 mt-2">
                                {isFullyVerified
                                  ? "Tap on an address to select it for delivery"
                                  : "Only verified addresses are selectable for delivery"
                                }
                              </p>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                              {addresses
                                .sort((a, b) => {
                                  // Sort by: default first, then home addresses, then work, then other
                                  const getPriority = (address: any) => {
                                    if (address.is_default) return 0;
                                    if (address.label?.toLowerCase() === 'home') return 1;
                                    if (address.label?.toLowerCase() === 'work') return 2;
                                    return 3;
                                  };

                                  const priorityA = getPriority(a);
                                  const priorityB = getPriority(b);

                                  if (priorityA !== priorityB) {
                                    return priorityA - priorityB;
                                  }

                                  // If same priority, sort by verification status
                                  if (a.is_verified !== b.is_verified) {
                                    return a.is_verified ? -1 : 1;
                                  }

                                  // Then by label alphabetical
                                  return (a.label || '').localeCompare(b.label || '');
                                })
                                .map((address) => {
                                  const isSelectable = isFullyVerified || address.is_verified;

                                  return (
                                    <div
                                      key={address.id}
                                      className={`transition-opacity duration-200 ${!isSelectable ? 'opacity-60' : ''
                                        }`}
                                    >
                                      <AddressCard
                                        address={address}
                                        isSelected={selectedAddressId === address.id}
                                        onClick={() => {
                                          if (isSelectable) {
                                            handleAddressSelect(address.id);
                                          }
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* Add New Address Button */}
                        <div>
                          <button
                            onClick={() => setShowAddressForm(true)}
                            className="w-full group relative overflow-hidden bg-gradient-to-r from-white to-gray-50 border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02]"
                          >
                            <div className="flex flex-col items-center justify-center">
                              <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                <Plus className="w-8 h-8 text-blue-600" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">Add New Address</h3>
                              <p className="text-gray-600 text-center max-w-md">
                                Add a new delivery location for this order
                              </p>
                              <div className="mt-4 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium">
                                Click to add address
                              </div>
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/50 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                          </button>
                        </div>
                        {/* No addresses state */}
                        {addresses.length === 0 && !showAddressForm && (
                          <div className="text-center py-12">
                            <div className="w-24 h-24 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                              <MapPin className="w-12 h-12 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">No Addresses Yet</h3>
                            <p className="text-gray-600 mb-8 max-w-md mx-auto">
                              Add your first delivery address to continue with your order. We'll verify it for future orders.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Back button when in all addresses view */}
                    {showAllAddresses && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <button
                          onClick={() => setShowAllAddresses(false)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <ArrowLeft className="w-5 h-5" />
                          Back to Default Address
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons for all addresses view */}
                  {showAllAddresses && selectedAddressId && (
                    <div className="px-6 sm:px-8 py-6 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200">
                      <div className="flex flex-col-reverse sm:flex-row gap-3">
                        <button
                          onClick={() => setShowAllAddresses(false)}
                          className="flex-1 sm:flex-none px-6 py-3.5 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
                        >
                          <ArrowLeft className="w-5 h-5 mr-2" />
                          Back
                        </button>
                        <button
                          onClick={handleProceedToReview}
                          disabled={!selectedAddressId}
                          className="flex-1 sm:flex-none px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 flex items-center justify-center"
                        >
                          Continue with Selected Address
                          <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Review - Mobile Optimized */}
            {step === 'review' && (
              <ReviewStep
                selectedAddressId={selectedAddressId}
                addresses={addresses}
                newAddressData={newAddressData}
                user={user}
                cart={cart}
                selectedDeliveryOption={selectedDeliveryOption}
                setSelectedDeliveryOption={setSelectedDeliveryOption}
                onEditAddress={() => setStep('address')}
                onPlaceOrder={handlePlaceOrder}
                isSubmitting={isSubmitting}
                onOptionsLoaded={handleOptionsLoaded}
              />
            )}

            {/* Step 3: Processing */}
            {step === 'processing' && (
              <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl border border-blue-200 shadow-lg overflow-hidden">
                <div className="p-12 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full mb-8 border border-blue-200">
                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Almost There!</h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto text-lg">
                    We're confirming your order details and preparing it for dispatch.
                  </p>
                  <div className="max-w-sm mx-auto">
                    <div className="h-2 bg-gradient-to-r from-gray-200 to-gray-100 rounded-full overflow-hidden mb-2">
                      <div className="h-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 rounded-full animate-pulse">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">Processing your order...</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-6">
              <CartSummary
                showCheckoutButton={false}
                isProcessing={step === 'processing' || isSubmitting}
                selectedDeliveryOption={selectedDeliveryOption}
              />

              {/* Security & Trust Badges */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 p-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-600" />
                  Secure Delivery Guarantee
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 p-2 bg-gradient-to-r from-green-100 to-green-50 rounded-lg mr-3">
                      <Shield className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Verified Addresses Only</p>
                      <p className="text-xs text-gray-600">All delivery locations are verified for safety</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 p-2 bg-gradient-to-r from-blue-100 to-blue-50 rounded-lg mr-3">
                      <Truck className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Order Tracking</p>
                      <p className="text-xs text-gray-600">Track your order from start to finish</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 p-2 bg-gradient-to-r from-purple-100 to-purple-50 rounded-lg mr-3">
                      <Lock className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Safe Payment</p>
                      <p className="text-xs text-gray-600">Pay securely on delivery</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Need Help? */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100 p-5">
                <h4 className="font-bold text-gray-900 mb-3">Need Help?</h4>
                <div className="space-y-2">
                  <button className="w-full text-left text-sm text-gray-700 hover:text-gray-900 p-3 hover:bg-blue-50 rounded-xl transition-colors flex items-center justify-between group">
                    <span>Delivery Support</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                  </button>
                  <button className="w-full text-left text-sm text-gray-700 hover:text-gray-900 p-3 hover:bg-blue-50 rounded-xl transition-colors flex items-center justify-between group">
                    <span>Order Changes</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                  </button>
                  <button className="w-full text-left text-sm text-gray-700 hover:text-gray-900 p-3 hover:bg-blue-50 rounded-xl transition-colors flex items-center justify-between group">
                    <span>Contact Customer Service</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Address Form Modal */}
      {showAddressForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <AddressForm
              cities={cities}
              onSubmit={handleAddressFormSubmit}
              onCancel={() => {
                setShowAddressForm(false);
                setNewAddressData(null);
              }}
              onSkipHouseNumber={() => { }}
              isHomeAddress={false}
              isVerifiedHome={false}
            />
          </div>
        </div>
      )}

      {verificationModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className={`p-2 rounded-full ${verificationModal.canProceed ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                {verificationModal.canProceed ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{verificationModal.title}</h3>
              </div>
            </div>

            <div className={`${verificationModal.canProceed ? 'bg-green-50' : 'bg-amber-50'
              } border rounded-lg p-4 mb-6 whitespace-pre-line text-sm`}>
              {verificationModal.message}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setVerificationModal(prev => ({ ...prev, isOpen: false }));
                  verificationModal.buttonAction();
                }}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                {verificationModal.buttonText}
              </button>
              <button
                onClick={() => {
                  setVerificationModal(prev => ({ ...prev, isOpen: false }));
                  if (verificationModal.canProceed) {
                    // Stay on checkout page
                  } else {
                    navigate('/cart'); // Return to cart
                  }
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {verificationModal.canProceed ? 'Return to Cart' : 'Back to Cart'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;